import type { TimeSlot } from "@clipboard-health/contract-worker-app-bff";
import { Stack } from "@mui/material";
import { formatTotalPayWithShiftBoost } from "@src/appV2/redesign/Shift/ShiftBoost/formatTotalPayWithShiftBoost";
import { DotSeparatedList, Pill, Text } from "@src/appV2/redesign/system/ui-components";
import {
  differenceInMinutes,
  formatDistanceToNowStrict,
  isAfter,
  isBefore,
  parseISO,
} from "date-fns";

import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { BookingCardContentWrapper } from "../CardContentWrapper";
import { ShiftType } from "../constants";
import { ShiftCardSummaryInfoDeprecated } from "../Open/CardSummaryInfoDeprecated";
import { ShiftCardPillsWrapper } from "../PillsWrapper";
import { BookingCardSummaryWrapper } from "../SummaryWrapper";
import { TimeSlotIndicator } from "../TimeSlotIndicator";
import { WorkerShiftCardFooter } from "./Footer";
import { ShiftCardWrapper } from "./Wrapper";

function getShiftStartsText(shiftStart: Date) {
  if (isBefore(new Date(), shiftStart)) {
    return `Starts in ${formatDistanceToNowStrict(shiftStart)}`;
  }

  return `Starts now`;
}

export interface MyShiftCardProps {
  shiftTimeSlot: TimeSlot;
  shiftStart: string;
  shiftEnd: string;
  shiftType?: ShiftType;
  workplaceName?: string;
  workplaceTimezone?: string;
  shiftHourlyPay?: string;
  shiftTotalPay?: number;
  shiftFormattedTotalPay?: string;
  shiftBoost?: number;
  breakDurationInMinutes: number;
  requiresBreak?: boolean;
  outlined?: boolean;
  isLoadingQualification?: boolean;
  qualificationText: string;
}

export function MyShiftCard(props: MyShiftCardProps) {
  const {
    outlined,
    shiftTimeSlot,
    shiftStart,
    shiftEnd,
    qualificationText,
    isLoadingQualification,
    shiftType,
    shiftHourlyPay,
    shiftTotalPay,
    shiftFormattedTotalPay,
    shiftBoost,
    breakDurationInMinutes,
    requiresBreak = false,
    workplaceName,
    workplaceTimezone,
  } = props;

  const startDate = parseISO(shiftStart);
  const endDate = parseISO(shiftEnd);
  const totalDurationInHours = differenceInMinutes(endDate, startDate) / 60;

  const totalWithBoostPay = formatTotalPayWithShiftBoost(shiftTotalPay, shiftBoost);

  return (
    <ShiftCardWrapper
      sx={{ width: "100%", minHeight: 0 }}
      outlined={outlined}
      isUrgent={shiftType === ShiftType.URGENT}
    >
      <BookingCardContentWrapper>
        <ShiftCardPillsWrapper>
          <Pill
            size="small"
            sx={{ width: "fit-content" }}
            label={
              <DotSeparatedList>
                <Text semibold variant="body2">
                  {formatDayOfMonthWithDayName(startDate, { timezone: workplaceTimezone })}
                </Text>
                <Text semibold variant="body2">
                  {getShiftStartsText(startDate)}
                </Text>
              </DotSeparatedList>
            }
          />
        </ShiftCardPillsWrapper>

        <Stack spacing={6}>
          <BookingCardSummaryWrapper>
            <TimeSlotIndicator timeSlot={shiftTimeSlot} />

            <ShiftCardSummaryInfoDeprecated
              dateRange={{ startDate, endDate }}
              timezone={workplaceTimezone}
              deviceTimezone={new Intl.DateTimeFormat().resolvedOptions().timeZone}
              durationInHours={totalDurationInHours}
              breakDurationInMinutes={breakDurationInMinutes}
              requiresBreak={requiresBreak}
              workplaceName={String(workplaceName)}
              useNowForStartDate={isAfter(new Date(), startDate)}
            />
          </BookingCardSummaryWrapper>

          <WorkerShiftCardFooter
            hourlyPay={shiftHourlyPay}
            totalPay={shiftFormattedTotalPay}
            totalWithBoostPay={totalWithBoostPay}
            qualificationText={qualificationText}
            isLoadingQualification={isLoadingQualification}
          />
        </Stack>
      </BookingCardContentWrapper>
    </ShiftCardWrapper>
  );
}
